import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'

export function slidesPerViewForBreakpoints(sliderPerViewObj, breakpoints) {
	return Object.entries(sliderPerViewObj).reduce(
		(acc, [key, value]) => ({
			...acc,
			[breakpoints.values[key]]: {
				slidesPerView: value,
			},
		}),
		{}
	)
}

/**
 * @description - Transforms the columns object key values to work with Grid.
 * @param {Object} props - The columns object.
 * @param {object} props.columns - columns object
 * @param {boolean} props.humanFriendly - if true, then return human friendly columns object,
 * else return the machine friendly columns object which works with Grid.
 * @returns {object} - human/machine friendly columns object.
 * @example
 * columnsConversion({ columns: { xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }, humanFriendly: false })
 * // returns { columns: 12, xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }
 */
export const columnsConversion = ({ columns, humanFriendly = true }) => {
	if (Object.keys(columns).length > 0) {
		if (!humanFriendly) {
			return Object.entries(columns).reduce(
				(acc, [key, value]) => ({
					...acc,
					[key]: 12 / value,
				}),
				{}
			)
		} else {
			return Object.entries(columns).reduce(
				(acc, [key, value]) => ({
					...acc,
					[key]: value / 12,
				}),
				{}
			)
		}
	} else {
		return {}
	}
}

/**
 * Debounces an async function in a way that can be awaited.
 * By default debounce doesn't return a promise for async functions unless it's invoking them immediately. See CUR-4769 for details.
 * @param func async function to debounce calls for.
 * @param wait same function as lodash.debounce's wait parameter.
 *             Call this function after this amount of time has passed since the last call.
 * @returns a promise which will be resolved/ rejected only if the function is executed, with the result of the underlying call.
 */
export const asyncDebounce = (func, wait) => {
	const debounced = debounce((resolve, reject, args) => {
		func(...args)
			.then(resolve)
			.catch(reject)
	}, wait)
	return (...args) =>
		new Promise((resolve, reject) => {
			debounced(resolve, reject, args)
		})
}

/**
 * Throttles an async function in a way that can be awaited.
 * By default throttle doesn't return a promise for async functions unless it's invoking them immediately. See CUR-4769 for details.
 * @param func async function to throttle calls for.
 * @param wait same function as lodash.throttle's wait parameter.
 *             Call this function at most this often.
 * @returns a promise which will be resolved/ rejected only if the function is executed, with the result of the underlying call.
 */
export const asyncThrottle = (func, wait) => {
	const throttled = throttle((resolve, reject, args) => {
		func(...args)
			.then(resolve)
			.catch(reject)
	}, wait)
	return (...args) =>
		new Promise((resolve, reject) => {
			throttled(resolve, reject, args)
		})
}
